import { styled, Theme, useTheme } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useConfirmDialog, } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { PageTab } from "app/routes/entity-administration/tabs";
import { NotAuthorized } from "common/components/not-authorized";
import Guid from "common/values/guid/guid";
import AdministratorInvitationsTable from "legal-entities/entities/administrator-invitation/view/components/administrator-invitations-table";
import EntityMembersTable from "legal-entities/entities/entity-member/view/components/entity-members-table";
import LegalEntityAdministrationTable from "legal-entities/entities/legal-entity/view/legal-entity-administration-table";
import EntityMemberInviteManager from "legal-entities/entities/user-entity-member-invitation/view/components/entity-member-invite-manager";
import ViewIndividualProfile from "marketplace/values/individual-profile/view/view-individual-profile";
import {
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  MRT_ShowHideColumnsButton as ToggleShowHideColumnsButton,
} from "material-react-table";
import PermissionsTable from 'permissions/entities/permission/view/components/permissions-table';
import * as React from "react";
import { JSX, useEffect } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "users/session/session-context";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));

export default function EntityAdministration() {
  const [activeTab, setActiveTab] = React.useState<PageTab>(PageTab.Users);
  const routeParams = useLoaderData();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const theme = useTheme();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;

    if (routeParams.tab) {
      setActiveTab(routeParams.tab as PageTab);
    } else {
      navigate(`/administration/${PageTab.Users}`);
    }

    if (routeParams.action === "invite") {
      handleInviteUsers();
      return;
    }

    if (routeParams.action === "view" && routeParams.id) {
      handleViewProfile(new Guid(routeParams.id));
    }
  }, [routeParams]);



  function handleViewProfile(individualId?: Guid): void {
    if (!individualId) return;

    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        titleStyle: {
          position: "absolute",
          right: 0,
          top: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
          />
        ),
      },
      () => navigate(parentPath.pathname)
    );
  }

  function handleInviteUsers() {
    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: "User Invitations",
        resetStack: true,
        MuiProps: {
          maxWidth: "md",
        },
        component: <EntityMemberInviteManager confirmDialog={confirm} />,
      },
      () => navigate(parentPath.pathname)
    );
  }

  const renderTopToolbarCustomActions = <T extends RowData,>(
    props: { table: TableInstance<T> },
    additional?: JSX.Element | undefined,
    filters?: JSX.Element | undefined,
  ): JSX.Element => {
    const { table } = props;

    const params = new URLSearchParams(location.search);
    const isManagingTemplates = params.get("action") === "manage-templates";

    return (
      <TableTopToolbarContainer>
        <TableTitle variant="h4">
          Administration
        </TableTitle>
        <LowerTopToolbarContainer>
          <Tabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_event, newValue) => navigate(`/administration/${newValue}`)}
          >
            <Tab value={PageTab.Users} label="Users" />
            <Tab value={PageTab.Invitations} label="Invitations" />
            <Tab value={PageTab.LegalEntities} label="Legal Entities" />
            <Tab
              value={PageTab.Permissions}
              label={
                isManagingTemplates
                  ? (
                    <>
                      <span style={{ color: theme.palette.text.secondary }}>
                        Permissions
                      </span>&#128898; Templates
                    </>
                  )
                  : "Permissions"
              }
            />
          </Tabs>
          {additional}
        </LowerTopToolbarContainer>
        {filters}
        {isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ToggleShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    );
  }

  if (!session.user?.companyEntityId || !session.user?.isCompanyManager) {
    return <NotAuthorized />;
  }
  if (activeTab === PageTab.Users) {
    return (
      <EntityMembersTable
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      />
    );
  } else if (activeTab === PageTab.Invitations) {
    return (
      <AdministratorInvitationsTable
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      />
    );
  } else if (activeTab === PageTab.LegalEntities) {
    return (
      <LegalEntityAdministrationTable
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      />
    );
  } else if (activeTab == PageTab.Permissions) {
    return (
      <PermissionsTable
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
      />
    );
  }
}
